import type { ReactNode } from "react";
import { ShieldAlert } from "lucide-react";
import { useCurrentUser } from "../../hooks/useAPI";
import { LoadingSpinner } from "./LoadingSpinner";
import { PageHeader } from "./PageHeader";

interface RequireSuperAdminProps {
    /** Human-readable name of the gated surface (rendered in the restricted-area message). */
    featureName: string;
    children: ReactNode;
}

/**
 * RequireSuperAdmin — route-level guard for founder/Adjudon-internal
 * surfaces (SuperAdminPanel, FounderAnalyticsPage, WitnessAdminPage).
 *
 * Mirrors the RequirePlan pattern: while currentUser loads, show a
 * spinner; if the user isn't superAdmin, render a friendly restricted-
 * area message instead of letting the underlying page render + its
 * data fetches 403 noisily.
 *
 * Backend is the real enforcement layer (every super-admin endpoint
 * has requireSuperAdmin middleware). This wrapper is for UX clarity:
 * a non-founder who clicks a stale link or types the URL by accident
 * sees a clean explanation instead of a half-rendered shell.
 */
export function RequireSuperAdmin({ featureName, children }: RequireSuperAdminProps) {
    const { data: currentUser, isLoading } = useCurrentUser();

    if (isLoading) {
        return (
            <div className="p-4">
                <LoadingSpinner />
            </div>
        );
    }

    if (!currentUser?.superAdmin) {
        return (
            <div className="px-6 py-6 space-y-6 max-w-3xl">
                <PageHeader title={featureName} subtitle="Founder access only" />
                <div className="adp-card">
                    <div className="flex items-start gap-3">
                        <ShieldAlert
                            className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5"
                            aria-hidden="true"
                        />
                        <div>
                            <p className="text-sm font-semibold text-foreground mb-1">Restricted area</p>
                            <p className="text-xs text-muted-foreground">
                                This surface is reserved for Adjudon internal operations. If you reached
                                this page via an old bookmark or shared link, no action is required —
                                head back to the dashboard.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return <>{children}</>;
}
